import { Menu } from 'lucide-react'
import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { interfaceMessages } from '@shared/i18n/interface-messages'
import { Sidebar } from '@shared/sidebar'

export const AppShell = ({ children }: PropsWithChildren) => {
	const [menuOpen, setMenuOpen] = useState(false)
	const location = useLocation()
	const mainRef = useRef<HTMLElement>(null)
	const previousPath = useRef(location.pathname)
	const focusMainAfterMenuClose = useRef(false)

	useEffect(() => {
		if (previousPath.current === location.pathname) return
		previousPath.current = location.pathname
		if (!focusMainAfterMenuClose.current) requestAnimationFrame(() => mainRef.current?.focus())
	}, [location.pathname])

	const closeMenuForNavigation = () => {
		focusMainAfterMenuClose.current = true
		setMenuOpen(false)
	}

	return (
		<div className='min-h-screen bg-background'>
			<a
				href='#main-content'
				className='sr-only fixed left-4 top-4 z-[100] rounded-md bg-background px-4 py-2 text-sm font-medium shadow-lg focus:not-sr-only'>
				Перейти к основному содержимому
			</a>
			<Sidebar />
			<header className='sticky top-0 z-30 flex h-16 items-center gap-3 border-b bg-background/95 px-4 backdrop-blur lg:hidden'>
				<Dialog open={menuOpen} onOpenChange={setMenuOpen}>
					<DialogTrigger asChild>
						<Button variant='outline' size='icon' aria-label='Открыть основное меню'>
							<Menu className='h-5 w-5' aria-hidden='true' />
						</Button>
					</DialogTrigger>
					<DialogContent
						showCloseButton={false}
						onCloseAutoFocus={event => {
							if (!focusMainAfterMenuClose.current) return
							event.preventDefault()
							focusMainAfterMenuClose.current = false
							mainRef.current?.focus()
						}}
						className='left-0 top-0 h-full w-72 max-w-none translate-x-0 translate-y-0 gap-0 rounded-none border-y-0 border-l-0 p-0 sm:rounded-none'>
						<DialogTitle className='sr-only'>Основное меню</DialogTitle>
						<DialogDescription className='sr-only'>Навигация по административным разделам.</DialogDescription>
						<Sidebar mobile onNavigate={closeMenuForNavigation} />
					</DialogContent>
				</Dialog>
				<div>
					<div className='text-sm font-semibold'>TISIT Admin</div>
					<div className='text-xs text-muted-foreground'>{interfaceMessages.shell.subtitle}</div>
				</div>
			</header>
			<main
				id='main-content'
				ref={mainRef}
				tabIndex={-1}
				className='min-h-screen focus:outline-none lg:pl-64'>
				<div className='mx-auto max-w-[1600px] p-4 sm:p-6 lg:p-8'>{children}</div>
			</main>
		</div>
	)
}
